<template>
  <Trip-Head :trip="trip" />
  <section>
    <div
      v-if="!isRewind"
      class="card tw-overflow-hidden"
      :class="{ 'map-card-maximized': maximized }"
    >
      <TripMap
        v-if="sectionPolyline"
        class="tw-relative tw-z-[2] tw-h-full tw-w-full tw-flex-grow"
        :section-polyline="sectionPolyline"
        :section-photos="trip.main_section.photos"
        @openImageId="openPhoto"
        @map="map = $event"
      >
        <button
          class="normalize-style tw-absolute tw-right-2 tw-top-2 tw-z-10 tw-rounded-lg tw-border tw-bg-white tw-p-2"
          @click="fullscreen"
        >
          <font-awesome-icon v-if="!maximized" fixed-width icon="expand" />
          <font-awesome-icon v-else fixed-width icon="compress" />
        </button>
      </TripMap>

      <!-- Trip statistics -->
      <section class="tw-grid tw-grid-cols-3 tw-gap-2 2xl:tw-grid-cols-6">
        <div class="stat-block">
          <span>
            <unit-item :value="trip.main_section.distance">
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          <br />
          <span class="tw-text-black/50">{{ $t('Distance') }}</span>
        </div>
        <div class="stat-block">
          <span
            ><strong><duration-item :duration="trip.main_section.duration" /></strong
          ></span>
          <br />
          <span class="tw-text-black/50">{{ $t('Duration') }}</span>
        </div>
        <div v-if="!trip.hide_max_speed" class="stat-block">
          <span>
            <unit-item
              current-unit="m/s"
              unit-to-metric="km/h"
              unit-to-imperical="mph"
              :value="trip.max_speed"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          <br />
          <span class="tw-text-black/50">{{ $t('Max Speed') }}</span>
        </div>
        <div class="stat-block">
          <span>
            <unit-item
              current-unit="m/s"
              unit-to-metric="km/h"
              unit-to-imperical="mph"
              :value="trip.avg_speed"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          <br />
          <span class="tw-text-black/50">{{ $t('avg speed') }}</span>
        </div>

        <div class="stat-block">
          <span>
            <unit-item
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="trip.main_section.elevation_climb"
              :number-format="{ maximumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          /
          <span>
            <unit-item
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="-trip.main_section.elevation_fall"
              :number-format="{ maximumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          <br />
          <span class="tw-text-black/50">{{ $t('Climb / Fall') }}</span>
        </div>
        <div class="stat-block">
          <span>
            <unit-item
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="trip.main_section.max_elevation"
              :number-format="{ maximumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          /
          <span>
            <unit-item
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="trip.main_section.min_elevation"
              :number-format="{ maximumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </unit-item>
          </span>
          <br />
          <span class="tw-text-black/50">{{ $t('Sea level (max/min)') }}</span>
        </div>
      </section>

      <nuxt-page :trip="trip" @update="updateTrip" />
    </div>

    <div v-if="!isRewind" class="tw-flex tw-flex-wrap tw-gap-4 tw-pt-5 lg:tw-flex-nowrap">
      <div class="tw-flex tw-flex-grow tw-flex-col tw-gap-4">
        <section
          v-if="trip.duplicate_of != null"
          class="card tw-relative tw-flex tw-items-center tw-gap-4 tw-px-6 tw-py-4"
        >
          <div class="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-riser-orange/10"></div>

          <FontAwesomeIcon icon="triangle-exclamation" class="tw-text-2xl tw-text-riser-orange" />

          <p>
            <NuxtLink v-if="isOwner" :to="`/trips/${trip.duplicate_of}`">
              {{ $t('challenge-trip-duplicate-owner-info-message') }}
            </NuxtLink>

            <template v-else>
              {{ $t('challenge-trip-duplicate-others-info-message') }}
            </template>
          </p>
        </section>

        <section
          v-if="trip.implausible"
          class="card tw-relative tw-flex tw-items-center tw-gap-4 tw-px-6 tw-py-4"
        >
          <div class="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-riser-orange/10"></div>

          <FontAwesomeIcon icon="triangle-exclamation" class="tw-text-2xl tw-text-riser-orange" />

          <p>{{ $t('challenge-trip-implausible-info-message') }}</p>
        </section>

        <section class="card tw-px-6 tw-py-4">
          <div class="tw-flex tw-items-center tw-justify-between">
            <h1 class="tw-truncate tw-text-lg tw-font-bold">{{ trip.title || $t('Trip') }}</h1>

            <span>
              <ClientOnly>
                {{ formatDate(new Date(trip.date)) }}

                <template #fallback> - </template>
              </ClientOnly>
            </span>
          </div>

          <p v-if="trip.note" class="tw-mt-2 tw-whitespace-pre-wrap">
            {{ trip.note }}
          </p>

          <div class="tw-mt-6">
            <AppLikeButton
              :is-liked="trip.liked_by_me"
              :like-count="trip.likes_count"
              :loading="likeLoading"
              @click="toggleLike"
              @count-click="showLikeList"
            />
          </div>
        </section>

        <section class="card tw-p-6">
          <comments
            type="trip"
            class="comments"
            :comments="comments"
            :endpoint="'trips/' + trip.id + '/comments'"
            :visible="10"
          />
        </section>
      </div>

      <div class="tw-flex tw-w-full tw-flex-shrink-0 tw-flex-col tw-gap-4 lg:tw-w-80 xl:tw-w-96">
        <div class="tw-flex tw-flex-col tw-gap-4">
          <router-link
            class="button blue-button tw-w-full tw-text-center"
            :to="'/trips/' + trip.id + '/share'"
          >
            {{ $t('Share') }}
          </router-link>

          <router-link
            class="button blue-button tw-w-full tw-text-center"
            :to="`/routeplanner?trip_id=${trip.id}`"
          >
            {{ $t('ride-this-trip') }}
          </router-link>

          <router-link
            v-if="isOwner"
            class="button black-button tw-w-full tw-text-center"
            :to="'/trips/' + trip.id + '/edit'"
          >
            {{ $t('Edit Trip') }}
          </router-link>

          <div v-if="isOwner" class="tw-relative">
            <button
              class="button black-button tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-text-center"
              @click="exportGPX"
            >
              <span>{{ $t('Export-as-gpx') }}</span>

              <IconsProBadge v-if="!isUserPro" class="tw-w-4" />
            </button>
          </div>
        </div>

        <RewindPreview v-if="route" :route="route" :trip="trip" :isPreview="true" />

        <TripPhotoCard
          v-if="sortedPhotos.length > 0"
          class="photos"
          :photos="sortedPhotos"
          :trip-id="trip.id"
        />

        <member-sidebar-item
          :showAllLink="`/trips/${trip.id}/participants`"
          v-if="isLoggedIn"
          :title="`${$t('Participants')}`"
          :members-count="participants.length"
          :members="participants"
        />
      </div>
    </div>
  </section>

  <nuxt-page v-if="isRewind" :trip="trip" @update="updateTrip" />
</template>

<script lang="ts">
import createGpx from 'gps-to-gpx'
import emojiTree from 'emoji-tree'
import FileSaver from 'file-saver'
import DurationItem from '~/components/duration-item.vue'
import Comments from '~/components/comments.vue'
import TripMap from '~/components/TripMap.vue'
import TripPhotoCard from '~/components/trip-photo-card.vue'
import MemberSidebarItem from '~/components/member-sidebar.vue'
import UnitItem from '~/components/unit-item.vue'
import { defineComponent } from 'vue'
import type { MainSectionPhoto, TripData } from '~/types/trip'
import { Map } from 'maplibre-gl'
import { type FeatureCollection } from 'geojson'
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

import type { PostingComment } from '~/types/postings'

interface IData {
  maximized: globalThis.Ref<boolean>
  route?: globalThis.Ref<FeatureCollection | undefined>
  trip: globalThis.Ref<TripData>
  sectionPolyline: globalThis.Ref<string | undefined>
  map?: Map
  comments: Array<PostingComment>
  likeLoading: globalThis.Ref<boolean>
}

export default defineComponent({
  name: 'Trip',
  components: {
    Comments,
    DurationItem,
    TripMap,
    MemberSidebarItem,
    UnitItem,
    TripPhotoCard
  },
  async setup(): Promise<IData> {
    const { params } = useRoute()
    const authStore = useAuthStore()

    const { getTrip } = useTripService()
    const { getSectionPolyline } = useSectionService()
    const { getComments } = useCommentsService()

    // If no trip id specified, redirect to user's roadbook
    if (!params.id) {
      navigateTo('/roadbook')
    }
    try {
      const [trip, comments] = await Promise.all([
        getTrip(params.id as string),
        authStore.session_data.user ? getComments('trips/' + params.id + '/comments') : []
      ])

      const sectionPolyline = await getSectionPolyline(trip.main_section.id)

      return {
        trip: ref(trip),
        comments: comments as Array<PostingComment>,
        maximized: ref(false),
        route: ref(undefined),
        likeLoading: ref(false),
        map: undefined,
        sectionPolyline: ref(sectionPolyline?.polyline)
      }
    } catch (err: any) {
      if (err.response.status === 404) {
        throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
      }
      throw err
    }
  },
  async created() {
    const { getSectionGeoJSON } = useSectionService()
    const { trackEvent } = useAnalyticsTrackingService()
    trackEvent(ANALYTICS_EVENTS.tripDetailShow())
    if (!this.trip) return
    const sectionID = this.trip.main_section.id
    const route = await getSectionGeoJSON(sectionID.toString())
    this.route = route ?? undefined
  },

  computed: {
    isUserPro() {
      const authStore = useAuthStore(this.$pinia)

      return authStore.isUserPro
    },
    session_token() {
      const authStore = useAuthStore(this.$pinia)

      return authStore.session_data.token
    },
    participants(): Array<Record<string, number>> {
      if (!this.trip) return []
      return [this.trip.user_id, ...this.trip.friend_ids].map((id) => ({ id }))
    },
    isOwner(): boolean {
      const authStore = useAuthStore(this.$pinia)

      if (authStore.currentUser) {
        return authStore.currentUser.id === this.trip?.user_id
      }
      return false
    },
    sortedPhotos(): Array<MainSectionPhoto> {
      if (!this.trip || !this.trip.main_section.photos) return []

      return [...this.trip.main_section.photos].sort(
        (a: MainSectionPhoto, b: MainSectionPhoto) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
    },
    isLoggedIn() {
      const authStore = useAuthStore(this.$pinia)

      return authStore.session_data.user
    },
    isRewind(): boolean {
      const { path } = useRoute()
      return path.includes('rewind')
    }
  },
  beforeUnmount() {
    const mainStore = useMainStore(this.$pinia)

    if (this.maximized) {
      mainStore.unhideScrollbar()
    }
  },
  methods: {
    showLikeList() {
      const mainStore = useMainStore(this.$pinia)

      mainStore.showLikeListModal('trips', this.trip.id)
    },
    openPhoto(photoId: number) {
      const router = useRouter()

      router.push({
        path: `/trips/${this.trip.id}/photos`,
        query: { pid: photoId }
      })
    },
    updateTrip(data: TripData) {
      this.trip = { ...this.trip, ...data }
    },
    fullscreen() {
      this.maximized = !this.maximized
      const map = this.map
      setTimeout(function () {
        map?.resize()
      }, 0)
      setTimeout(function () {
        map?.resize()
      }, 1000) // TODO: Check for browser and only call 1000ms resize for Safari
    },
    async toggleLike() {
      if (!this.trip) return

      this.likeLoading = true

      const { likeTrip, unlikeTrip } = useLikeService()

      try {
        if (this.trip.liked_by_me) {
          await unlikeTrip(this.trip.id)

          this.trip.liked_by_me = false
          this.trip.likes_count--
        } else {
          await likeTrip(this.trip.id)

          this.trip.liked_by_me = true
          this.trip.likes_count++
        }
      } catch (error) {
        console.error('Error toggling like', error)
      } finally {
        this.likeLoading = false
      }
    },
    async exportGPX() {
      const { getSectionLocations } = useSectionService()
      const { requirePro } = useProGate()

      const trip = this.trip

      requirePro('gpx_export_trip', async () => {
        if (!trip) return
        try {
          const tree = emojiTree(trip?.title)
          const title = tree
            .filter(({ type }: { type: string }) => type !== 'emoji')
            .map(({ text }: { type: string; text: string }) => text)
            .join('')

          const locations = await getSectionLocations(trip.main_section.id)

          if (!locations) return

          const waypoints = locations.map((point) => ({
            ...point,
            elevation: point.altitude,
            time: point.timestamp
          }))

          const gpx = createGpx(waypoints, {
            activityName: title,
            startTime: trip.date,
            creator: 'riserapp.com'
          })
          const blob = new Blob([gpx], { type: 'text/plain;charset=utf-8' })
          FileSaver.saveAs(blob, `${trip.title}_Riser.gpx`)
        } catch (err) {
          console.error(err)
          throw err
          // TODO: Show something went wrong dialog
        }
      })
    }
  }
})
</script>

<style scoped>
.stat-block {
  @apply tw-flex-1 tw-px-1 tw-py-2;
}

.map-card-maximized {
  @apply tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-top-bar tw-z-[100] tw-flex tw-h-auto tw-w-auto tw-flex-col;
}
</style>
